<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <base-alert-dialog
      :show="alertDialog.show"
      :variant="alertDialog.variant"
      :title="alertDialog.title"
      :message="alertDialog.message"
      @confirm="alertDialog.show = false"
      @cancel="alertDialog.show = false"
    />

    <base-alert-dialog
      :show="confirmDialog.show"
      variant="warning"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      show-cancel
      :confirm-text="confirmDialog.confirmText"
      confirm-variant="danger"
      @confirm="resolveConfirm(true)"
      @cancel="resolveConfirm(false)"
    />
    <div class="row">
      <div class="col">
        <card>
          <tab-rail :tabs="[{ name: 'Projects' }]" :value="0" class="mb-3">
            <template #actions>
              <base-button v-if="hasPermissions('project_update')" size="sm" v-on:click="addProject()" type="primary">
                <i class="tim-icons icon-simple-add mr-2"></i> Create project</base-button>
            </template>
          </tab-rail>

          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :loading="loading"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="projects"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          ></filter-bar>

          <div v-if="projects.length <= 0 && !loading" class="text-center py-5">
            <span style="font-size: 3em; color: rgba(225, 78, 202, 0.4)">
              <i class="tim-icons icon-app"></i>
            </span>
            <h4 class="mt-3 text-white">{{ hasActiveFilters ? 'No projects match your filters' : 'No projects yet' }}</h4>
            <p class="text-muted">{{ hasActiveFilters ? 'Try clearing a filter.' : 'Projects help you organize and track work items.' }}</p>
            <base-button v-if="hasPermissions('project_update') && !hasActiveFilters" size="sm" type="primary" @click="addProject()">
              <i class="tim-icons icon-simple-add mr-2"></i> Create your first project
            </base-button>
          </div>

          <div class="d-none d-sm-block">
            <div class="row" >
              <div class="col">
                <b-table v-if="projects.length > 0" striped hover :items="projects"
                         :fields="tableFields">
                  <template v-slot:cell(project_name)="row">
                    <a target="_blank" :href="'/projects/' + row.item.id">{{row.item.name}}</a>
                    <small class="text-muted d-block">{{row.item.description}}</small>
                  </template>
                  <template v-slot:cell(key)="row">
                    <div>{{row.item.identifier}}</div>
                  </template>
                  <template v-slot:cell(type)="row">
                    <badge v-if="row.item.project_type === 'bookings'" type="info">Bookings</badge>
                    <badge v-else type="default">Default</badge>
                  </template>
                  <template v-slot:cell(last_project_update)="row">
                    <el-tooltip :content="getLocalDateTime(row.item.last_project_update)" placement="top">
                      <span>{{getLastUpdatedLabel(row.item.last_project_update)}}</span>
                    </el-tooltip>
                  </template>
                  <template v-slot:cell(created_by)="row">
                    <div v-if="row.item.project_type === 'bookings'">System</div>
                    <div v-else>{{row.item.first_name}} {{row.item.last_name}}</div>
                  </template>
                  <template v-slot:cell(actions)="row">
                    <el-dropdown>
                      <base-button
                        simple
                        type="primary"
                        link
                      > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                      </base-button>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <a class="dropdown-item mt-2 mb-2" target="_blank" :href="'/projects/' + row.item.id">
                            <i class="tim-icons icon-app mr-2"></i> Go to project
                          </a>
                          <a class="dropdown-item mt-2 mb-2" v-if="hasPermissions('project_update') && row.item.project_type !== 'bookings'" @click.prevent="editProject(row.item)" href="#">
                            <i class="tim-icons icon-pencil mr-2"></i> Edit project
                          </a>
                          <hr v-if="hasPermissions('project_delete') && row.item.project_type !== 'bookings'">
                          <a class="dropdown-item mt-2 mb-2" v-if="hasPermissions('project_delete') && row.item.project_type !== 'bookings'" @click.prevent="deleteProjectPrompt(row.item)" href="#">
                            <i class="tim-icons icon-trash-simple mr-2"></i> Archive project
                          </a>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </template>
                </b-table>
              </div>
            </div>
          </div>

          <div class="d-block d-sm-none">
            <div class="row" >
              <div class="col">
                <b-table v-if="projects.length > 0" striped hover :items="projects"
                         :fields="['projects']">
                  <template #head(projects)="row">
                    <span>Project count: {{totalRows}}</span>
                  </template>
                  <template v-slot:cell(projects)="row">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <a target="_blank" :href="'/projects/' + row.item.id">{{row.item.name}}</a>
                        <badge v-if="row.item.project_type === 'bookings'" type="info" class="ml-2" style="font-size: 10px">Bookings</badge>
                      </div>
                      <el-dropdown>
                        <base-button
                          simple
                          type="primary"
                          link
                        > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                        </base-button>
                        <template #dropdown>
                          <el-dropdown-menu>
                            <a class="dropdown-item mt-2 mb-2" target="_blank" :href="'/projects/' + row.item.id">
                              <i class="tim-icons icon-app mr-2"></i> Go to project
                            </a>
                            <a class="dropdown-item mt-2 mb-2" v-if="hasPermissions('project_update') && row.item.project_type !== 'bookings'" @click.prevent="editProject(row.item)" href="#">
                              <i class="tim-icons icon-pencil mr-2"></i> Edit project
                            </a>
                            <hr v-if="hasPermissions('project_delete') && row.item.project_type !== 'bookings'">
                            <a class="dropdown-item mt-2 mb-2" v-if="hasPermissions('project_delete') && row.item.project_type !== 'bookings'" @click.prevent="deleteProjectPrompt(row.item)" href="#">
                              <i class="tim-icons icon-trash-simple mr-2"></i> Archive project
                            </a>
                          </el-dropdown-menu>
                        </template>
                      </el-dropdown>
                    </div>
                    <small class="text-muted d-block">{{row.item.description}}</small>
                    <small class="text-muted">
                      {{row.item.project_type === 'bookings' ? 'System' : row.item.first_name + ' ' + row.item.last_name}}
                      &middot; {{getLastUpdatedLabel(row.item.last_project_update)}}
                    </small>
                  </template>
                </b-table>
              </div>
            </div>
          </div>
          <div v-if="projects.length > 0">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              v-on:input="getProjects(perPage, getOffset())"
            ></b-pagination>
          </div>
        </card>
      </div>
    </div>

    <base-drawer :show="modals.create_project"
                 class="drawer-form"
                 :title="project.hasOwnProperty('id') ? 'Edit project' : 'Create project'"
                 @close="closeProjectDrawer">
      <drawer-section title="Details" icon="icon-app">
        <base-input required label="Name" :error="errs.name" v-model="project.name" placeholder="Name"></base-input>

        <label>
          <div>Key *
            <el-popover trigger="hover" placement="right">
              <div>
                <div class="popover-body">Choose a descriptive prefix for your project's <br/>
                  work item keys to recognize work from this project.<br/>
                  Limit to 10 characters.
                </div>
              </div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </div>
        </label>
        <base-input :error="errs.key" required v-model="project.key" placeholder="Key" @input="keyManuallyEdited = true"></base-input>

        <base-input label="Description">
          <el-input
            v-model="project.description"
            :rows="5"
            type="textarea"
            placeholder="Description"
          />
        </base-input>
      </drawer-section>

      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" :disabled="loading" @click="closeProjectDrawer">Cancel</base-button>
          <base-button v-if="project.hasOwnProperty('id')" type="primary" size="sm" :loading="loading" :disabled="loading" @click="updateProject(project)">Save</base-button>
          <base-button v-else type="primary" size="sm" :loading="loading" :disabled="loading" @click="createProject()">Create</base-button>
        </div>
      </template>
    </base-drawer>
  </div>
</template>

<script>
import {BPagination, BTable} from "bootstrap-vue";
import {Dropdown, DropdownMenu, Tooltip} from "element-ui";
import {Badge} from "@/components";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import TabRail from '@/components/TabRail';
import {Auth} from "aws-amplify";
import moment from "moment";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";

export default {
  name: "ProjectList",
  components: {
    TabRail,
    BTable,
    BPagination,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [Tooltip.name]: Tooltip,
    Badge
  },
  data() {
    return {
      loading: false,
      alertDialog: { show: false, title: '', message: '', variant: 'info' },
      confirmDialog: { show: false, title: '', message: '', confirmText: 'Confirm', resolve: null },
      projects: [],
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      perPageOptions: [5, 10, 25, 50],
      currentPage: 1,
      perPage: 10,
      totalRows: 10,
      // Flat filter values for the shared <filter-bar>. Seeded from the URL;
      // saved-views precedence is resolved by FilterBar.
      filterValues: this.buildInitialFilterValues(),
      didInitialFetch: false,
      project: {
        name: '',
        description: '',
        key: ''
      },
      errs: {
        key: '',
        name: ''
      },
      keyManuallyEdited: false,
      tableFields: [
        { key: 'project_name', label: 'Project name' },
        { key: 'key', label: 'Key' },
        { key: 'type', label: 'Type' },
        { key: 'created_by', label: 'Created by' },
        { key: 'last_project_update', label: 'Last updated' },
        { key: 'actions', label: 'Actions', class: 'text-right' }
      ],
      modals: {
        create_project: false
      }
    }
  },
  watch: {
    'modals.create_project'(open) {
      if (open) {
        document.addEventListener('keydown', this._onProjectDrawerEsc)
      } else {
        document.removeEventListener('keydown', this._onProjectDrawerEsc)
      }
    },
    'project.name'(val) {
      if (!this.project.id && !this.keyManuallyEdited && val) {
        this.project.key = val.replace(/[^a-zA-Z0-9]/g, '').substring(0, 5).toUpperCase()
      }
    }
  },
  computed: {
    filterSchema() {
      return [
        {key: 'name', type: 'text', label: 'Project name', placeholder: 'Project name', operatorLabel: 'contains', primary: true},
        {key: 'key', type: 'text', label: 'Key', placeholder: 'Key', operatorLabel: 'contains', primary: true},
        {key: 'type', type: 'select', label: 'Type', primary: true,
          options: [{label: 'Default', value: 'default'}, {label: 'Bookings', value: 'bookings'}]},
      ]
    },
    hasActiveFilters() {
      return !!this.filterValues.name || !!this.filterValues.key || !!this.filterValues.type
    },
  },
  methods: {
    editProject(item){
      this.project = {... item}
      this.project.key = item.identifier
      this.errs.name = ''
      this.errs.key = ''
      this.modals.create_project = true
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    getLastUpdatedLabel(dateString) {
      const now = moment();
      const inputDate = moment(dateString);
      const diffInDays = now.diff(inputDate, 'days');
      const diffInWeeks = now.diff(inputDate, 'weeks');
      const diffInMonths = now.diff(inputDate, 'months');
      const diffInYears = now.diff(inputDate, 'years');

      if (diffInDays < 1) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInWeeks === 1) return 'Last week';
      if (diffInMonths < 1) return `${diffInWeeks} weeks ago`;
      if (diffInMonths === 1) return 'Last month';
      if (diffInMonths < 12) return `${diffInMonths} months ago`;
      if (diffInYears === 1) return 'Last year';
      return `${diffInYears} years ago`;
    },
    getLocalDateTime(d){
      if(!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if(!date && !time) return ''
      return `${date}, ${time}`
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async addProject(){
      this.keyManuallyEdited = false
      this.project = {
        name: '',
        description: '',
        key: ''
      }
      this.modals.create_project = true
    },
    isUnique(project){
      for(let n in this.projects){
        if(!project.hasOwnProperty('id') || this.projects[n].id !== project.id){
          if(this.projects[n].identifier === project.key){
            return false
          }
        }
      }
      return true
    },
    validateFields(){
      let errors = 0

      if(this.project.name === ''){
        this.errs.name = 'Project name cannot be empty'
        errors++
      } else {
        this.errs.name = ''
      }

      if(this.project.key === ''){
        this.errs.key = 'Project key cannot be empty'
        errors++
      } else if(!this.isUnique(this.project)){
        this.errs.key = 'Project key must be unique'
        errors++
      } else {
        this.errs.key = ''
      }

      return !(errors > 0)
    },
    async deleteProjectPrompt(item){
      const ok = await this.askConfirm('Archive this project?', 'This action will archive the project and it will no longer be accessible.', 'Archive')
      if (ok) this.deleteProject(item)
    },
    showAlert(title, message, variant) {
      this.alertDialog = { show: true, title, message, variant }
    },
    askConfirm(title, message, confirmText) {
      return new Promise(resolve => {
        this.confirmDialog = { show: true, title, message, confirmText, resolve }
      })
    },
    resolveConfirm(value) {
      const resolve = this.confirmDialog.resolve
      this.confirmDialog = { show: false, title: '', message: '', confirmText: 'Confirm', resolve: null }
      if (resolve) resolve(value)
    },

    async deleteProject(item){
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects/' + item.id

      axios.delete(url, config)
        .then(response => {
          this.loading = false
          this.showAlert('Success', 'Project has been archived', 'success')
          this.getProjects(this.perPage, this.getOffset())
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async updateProject(item){
      if(!this.validateFields()){
        return
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects/' + item.id

      let data = this.project

      axios.put(url, data, config)
        .then(response => {
          this.loading = false
          this.modals.create_project = false
          this.getProjects(this.perPage, this.getOffset())
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async createProject(){
      if(!this.validateFields()){
        return
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects'

      let data = this.project

      axios.post(url, data, config)
        .then(response => {
          this.loading = false
          this.modals.create_project = false
          this.getProjects(this.perPage, this.getOffset())
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async getProjects(count, offset){
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects?count=' + count + '&offset=' + offset
        + '&name=' + encodeURIComponent(this.filterValues.name)
        + '&key=' + encodeURIComponent(this.filterValues.key)
        + '&type=' + encodeURIComponent(this.filterValues.type)

      axios.get(url, config)
        .then(response => {
          this.loading = false
          this.projects = response.data.projects
          this.totalRows = response.data.total_rows

          // If current page is beyond available data, reset to page 1
          if (this.totalRows > 0 && offset >= this.totalRows) {
            this.currentPage = 1
            this.getProjects(this.perPage, this.getOffset())
            return
          }

          this.addQueryParameters()
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    closeProjectDrawer() {
      this.modals.create_project = false
    },
    getOffset(){
      return Number((this.currentPage * this.perPage) - this.perPage)
    },
    buildInitialFilterValues() {
      const q = this.$route.query || {}
      return {
        name: q.name || '',
        key: q.key || '',
        type: q.type === 'default' || q.type === 'bookings' ? q.type : '',
      }
    },
    onFiltersChanged(values) {
      this.filterValues = {
        name: values.name || '',
        key: values.key || '',
        type: values.type || '',
      }
      if (!this.didInitialFetch) {
        // FilterBar (saved-filters-key) owns the first fetch. Preserve the URL page on it.
        this.didInitialFetch = true
      } else {
        this.currentPage = 1
      }
      this.getProjects(this.perPage, this.getOffset())
    },
    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.getProjects(this.perPage, this.getOffset())
    },
    addQueryParameters() {
      this.$nextTick(() => {
        this.currentPage = this.$route.query.page
      });

      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
      }
      delete newQuery.name; delete newQuery.key; delete newQuery.type
      if (this.filterValues.name) newQuery.name = this.filterValues.name
      if (this.filterValues.key) newQuery.key = this.filterValues.key
      if (this.filterValues.type) newQuery.type = this.filterValues.type
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
  },
  created() {
    this._onProjectDrawerEsc = (e) => {
      if (e.key === 'Escape' && this.modals.create_project) {
        this.closeProjectDrawer()
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onProjectDrawerEsc)
  },
  mounted() {
    this.perPage = this.$route.query.count ? Number(this.$route.query.count) : 10
    this.currentPage = this.$route.query.page ? Number(this.$route.query.page) : 1
    // FilterBar (saved-filters-key) owns the first fetch and emits its initial @change on mount.
  }
}
</script>

